import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { NextSeasonRequest, Season } from '../types/ScheduleTypes';


interface SeasonContextType {
  seasons: Season[];
  currentSeason: Season | null;
  selectedSeason: Season | null;
  isLoading: boolean;
  error: string | null;
  setSelectedSeason: (season: Season) => void;
  createNextSeason: (request: NextSeasonRequest) => Promise<void>;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

export const SeasonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [currentSeason, setCurrentSeason] = useState<Season | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchSeasons = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/seasons/`);
      const seasonsData = response.data;
      setSeasons(seasonsData);
      
      // Set current season
      const current = seasonsData.find((s: Season) => s.is_current);
      setCurrentSeason(current || null);
      
      // Set selected season to current season if not already set
      if (!selectedSeason && current) {
        setSelectedSeason(current);
      }
    } catch (error) {
      console.error('Error fetching seasons:', error);
      setError('Failed to load seasons');
    } finally {
      setIsLoading(false);
    }
  };

  const createNextSeason = async (request: NextSeasonRequest) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/seasons/`, request);
      const newSeason = response.data;
      setSeasons(prev => [...prev, newSeason]);
      setSelectedSeason(newSeason);
    } catch (error) {
      console.error('Error creating next season:', error);
      throw new Error('Failed to create next season');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSeasons();
    }
  }, [isAuthenticated]);

  return (
    <SeasonContext.Provider 
      value={{ 
        seasons, 
        currentSeason, 
        selectedSeason, 
        isLoading, 
        error, 
        setSelectedSeason,
        createNextSeason
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
};

export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (context === undefined) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
}; 